import type React from "react";
import { useContext, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { classNames } from "primereact/utils";
import { LayoutContext } from "./context/layoutcontext";
import { MenuContext } from "./context/menucontext";
import { useSubmenuOverlayPosition } from "./hooks/useSubmenuOverlayPosition";
import type { AppMenuItemProps } from "../types/layout";

const AppMenuitem = (props: AppMenuItemProps) => {
  const { activeMenu, setActiveMenu } = useContext(MenuContext);
  const { isDesktop, setLayoutState, layoutState } = useContext(LayoutContext);
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;
  const submenuRef = useRef(null);
  const menuitemRef = useRef(null);
  const item = props.item;
  const key = props.parentKey
    ? props.parentKey + "-" + props.index
    : String(props.index);
  const isActiveRoute = item?.to && pathname === item.to;
  const active =
    activeMenu === key || !!(activeMenu && activeMenu.startsWith(key + "-"));

  useSubmenuOverlayPosition({
    target: menuitemRef.current,
    overlay: submenuRef.current,
    container:
      menuitemRef.current &&
      menuitemRef.current.closest(".layout-menu-container"),
    when: props.root && active && isDesktop(),
  });

  useEffect(() => {
    if (layoutState.resetMenu) {
      setActiveMenu("");
      setLayoutState((prevLayoutState) => ({
        ...prevLayoutState,
        resetMenu: false,
      }));
    }
  }, [layoutState.resetMenu, setActiveMenu, setLayoutState]);
  useEffect(() => {
    if (item?.to && pathname === item.to) {
      setActiveMenu(key);
    }
  }, [pathname, item?.to, key, setActiveMenu]);

  useEffect(() => {
    const url = pathname + search;

    const onRouteChange = (url: string) => {
      if (item?.to && item.to === url) {
        setActiveMenu(key);
      }
    };

    onRouteChange(url);
  }, [pathname, search, item?.to, key, setActiveMenu]);

  const itemClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (item?.disabled) {
      event.preventDefault();
      return;
    }

    // execute command
    if (item?.command) {
      item.command({ originalEvent: event, item: item });
    }

    // toggle active state
    if (item?.items) {
      setActiveMenu(active ? props.parentKey || "" : key);
    } else {
      if (!isDesktop()) {
        setLayoutState((prevLayoutState) => ({
          ...prevLayoutState,
          staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive,
        }));
      }

      setActiveMenu(key);
    }
  };

  const subMenu =
    item?.items && item.visible !== false ? (
      <ul ref={submenuRef}>
        {item.items.map((child, i) => (
          <AppMenuitem
            item={child}
            index={i}
            className={child.badgeClass}
            parentKey={key}
            key={child.label}
          />
        ))}
      </ul>
    ) : null;

  return (
    <li
      ref={menuitemRef}
      className={classNames({
        "layout-root-menuitem": props.root,
        "active-menuitem": active,
      })}
    >
      {props.root && item?.visible !== false && (
        <div className="layout-menuitem-root-text">{item.label}</div>
      )}

      {(!item?.to || item?.items) && item?.visible !== false ? (
        <a
          href={item?.url}
          onClick={itemClick}
          className={classNames(item?.class, "p-ripple tooltip-target")}
          target={item?.target}
          data-pr-tooltip={item?.label}
          data-pr-disabled={false}
          tabIndex={0}
        >
          <i className={classNames("layout-menuitem-icon", item?.icon)}></i>
          <span className="layout-menuitem-text">{item?.label}</span>
          {item?.items && (
            <i className="pi pi-fw pi-angle-down layout-submenu-toggler"></i>
          )}
          <Ripple />
        </a>
      ) : null}

      {item?.to && !item?.items && item?.visible !== false ? (
        <Link
          to={item.to}
          replace={item.replaceUrl}
          onClick={itemClick}
          className={classNames(item.class, "p-ripple", {
            "active-route": isActiveRoute,
          })}
          tabIndex={0}
        >
          <i className={classNames("layout-menuitem-icon", item.icon)}></i>
          <span className="layout-menuitem-text">{item.label}</span>
          <Ripple />
        </Link>
      ) : null}

      {subMenu}
    </li>
  );
};

export default AppMenuitem;
